// * Import required lib
import { Route, Routes } from "react-router-dom";
import { lazy, Suspense } from "react";

// * Import Screens
import Loader from "../components/Loader";
import AccountSetup from "../screens/AccountSetup";
import PageNotFound from "../screens/PageNotFound/PageNotFound";
import NoAccess from "../screens/NoAccess/NoAccess";
import LayoutTopSideBottom from "../layout/LayoutTopSideBottom/LayoutTopSideBottom";
import ProtectedRoute from "./ProtectedRoute";
import RapidRegisterContainer from "../screens/Rapid/Screen/RapidRegisterContainer";
import RapidEditContainer from "../screens/Rapid/Screen/RapidEditContainer";
import AdminRapidEditContainer from "../screens/Rapid/Screen/RapidEditContainerAdmin";
import EditRapidFinishedContainer from "../screens/Rapid/Screen/EditRapidFinishedContainer";
import AdminLandingPageContainer from "../screens/Rapid/Screen/AdminLandingPageContainer";
import Home from "../screens/Home/Home";
import APP_CONSTANTS from "../utils/config";
import BrainLayout from "../screens/Brain2.0/layout/BrainLayout";
import Reviewer from "../screens/Brain2.0/screens/Reviewer";
import Approver from "../screens/Brain2.0/screens/Approver";
import Dashboard from "../screens/Brain2.0/screens/Dashboard";
import Submissions from "../screens/Brain2.0/screens/Submissions";
import FunctionAccess from "../screens/Brain2.0/screens/FunctionAccess";
import SSOT from "../screens/Brain2.0/screens/SSOT";
import AccessManagement from "../screens/Brain2.0/screens/AccessManagementNew";
import ApproverDetailPage from "../screens/Brain2.0/screens/ApproverDetailPage";
import SubmissionDetailPage from "../screens/Brain2.0/screens/SubmissionDetailPage";
import HarborLayout from "../screens/Harbor/layout/HarborLayout";
// const BillingDashboard = lazy(() =>
//   import("../screens/Rapid/Screen/BillingDashboard")
// );
const AppContainer = lazy(() => import("../screens/AppContainer/AppContainer"));
const DataProductDetails = lazy(() =>
  import("../screens/Explore/AllReportData/DataProductDetails2")
);

const DevOpsLandingPageContainer = lazy(() =>
  import("../screens/Rapid/Screen/DevOpsLandingPageContainer")
);
const DevOpsDetailsPage = lazy(() =>
  import("../screens/Rapid/Screen/DevOpsDetailsPage")
);
const RegisterContainer = lazy(() =>
  import("../screens/MyApps/RegisterScreens/RegisterContainer")
);
const ApplicationDetails = lazy(() =>
  import("../screens/ApplicationDetails/ApplicationDetails")
);
const EditApplication = lazy(() =>
  import("../screens/MyApps/RegisterScreens/EditApplication")
);
const EditGroupReport = lazy(() =>
  import("../screens/MyApps/RegisterScreens/EditReport/EditGroupReport")
);
const ApprovalsDetails = lazy(() =>
  import("../screens/Approvals/ApprovalsDetails")
);
const Approvals = lazy(() => import("../screens/Approvals/Approvals"));
const WorkInProgress = lazy(() =>
  import("../components/WorkInProgress/WorkInProgress")
);
const Admin = lazy(() => import("../screens/Approvals/Admin"));
const NewTicket = lazy(() => import("../components/Help/NewTicket"));
const TicketDetails = lazy(() => import("../components/Help/TicketDetails"));
const BrandMaintenanceHomePage = lazy(() =>
  import("../screens/Approvals/BrandMaintainence/BrandMaintenanceHomePage")
);
const KPIsDashboard = lazy(() =>
  import("../screens/Approvals/KPIsDashboard/KPIsDashboard")
);
const DataGovernanceHomepage = lazy(() =>
  import("../screens/Approvals/DataGovernance/DataGovernanceHomePage")
);
const SigviewAutomationLandingPage = lazy(() =>
  import("../screens/Approvals/SigviewAutomation/LandingPage")
);

const SigviewAutomationForm = lazy(() =>
  import("../screens/Approvals/SigviewAutomation/SigviewAutomationForm")
);

// Harbor Components
const HarborAccessManagement = lazy(() =>
  import("../screens/Harbor/screens/HarborAccessManagement")
);

const HarborReviewerPage = lazy(() =>
  import("../screens/Harbor/screens/HarborReviewer")
);

const HarborMySubmissions = lazy(() =>
  import("../screens/Harbor/screens/HarborMySubmissions")
);

const HarborSubmissionDetails = lazy(() =>
  import("../screens/Harbor/screens/HarborSubmissionDetails")
);

const HarborFunctionAccess = lazy(() =>
  import("../screens/Harbor/screens/HarborFunctionAccess")
);

const HarborDashboard = lazy(() =>
  import("../screens/Harbor/screens/HarborDashboard")
);

const HarborApproverPage = lazy(() =>
  import("../screens/Harbor/screens/HarborApprover")
);

const HarborApproverDetailPage = lazy(() =>
  import("../screens/Harbor/screens/HarborApproverDetailPage")
);

const HarborKnowledgeHub = lazy(() =>
  import("../screens/Harbor/screens/HarborKnowledgeHub")
);

const HarborSsot = lazy(() => import("../screens/Harbor/screens/HarborSsot"));

const UploadHarborReviewData = lazy(() =>
  import("../screens/Harbor/screens/UploadReviewData")
);

const AppRoutes = () => {
  return (
    <Suspense fallback={<Loader />}>
      <Routes>
        <Route path="/account-setup" element={<AccountSetup />} exact />
        <Route path="/no-access" element={<NoAccess />} exact />
        <Route path="/" element={<LayoutTopSideBottom />}>
          <Route path="/brain" element={<BrainLayout />}>
            <Route
              path="/brain/access-management"
              element={
                <Suspense fallback={<Loader />}>
                  <AccessManagement />
                </Suspense>
              }
              exact
            />
            <Route
              path="/brain/function-access"
              element={
                <Suspense fallback={<Loader />}>
                  <FunctionAccess />
                </Suspense>
              }
              exact
            />
            <Route
              path="/brain/ssot"
              element={
                <Suspense fallback={<Loader />}>
                  <SSOT />
                </Suspense>
              }
              exact
            />
            <Route
              path="/brain/reviewer"
              element={
                <Suspense fallback={<Loader />}>
                  <Reviewer />
                </Suspense>
              }
              exact
            />
            <Route
              path="/brain/approver"
              element={
                <Suspense fallback={<Loader />}>
                  <Approver />
                </Suspense>
              }
              exact
            />
            <Route
              path="/brain/approver/:id"
              element={
                <Suspense fallback={<Loader />}>
                  <ApproverDetailPage />
                </Suspense>
              }
              exact
            />
            <Route
              path="/brain/"
              element={
                <Suspense fallback={<Loader />}>
                  <Dashboard />
                </Suspense>
              }
              exact
            />
            <Route
              path="/brain/submissions"
              element={
                <Suspense fallback={<Loader />}>
                  <Submissions />
                </Suspense>
              }
              exact
            />
            <Route
              path="/brain/submissions/:id"
              element={
                <Suspense fallback={<Loader />}>
                  <SubmissionDetailPage />
                </Suspense>
              }
              exact
            />
          </Route>
          <Route path="/harbor" element={<HarborLayout />}>
            <Route
              path="/harbor/"
              element={
                <Suspense fallback={<Loader />}>
                  <HarborDashboard />
                </Suspense>
              }
              exact
            />
            <Route
              path="/harbor/access-management"
              element={
                <Suspense fallback={<Loader />}>
                  <HarborAccessManagement />
                </Suspense>
              }
              exact
            />
            <Route
              path="/harbor/reviewer"
              element={
                <Suspense fallback={<Loader />}>
                  <HarborReviewerPage />
                </Suspense>
              }
              exact
            />
            <Route
              path="/harbor/reviewer/upload"
              element={
                <Suspense fallback={<Loader />}>
                  <UploadHarborReviewData />
                </Suspense>
              }
              exact
            />
            <Route
              path="/harbor/submissions"
              element={
                <Suspense fallback={<Loader />}>
                  <HarborMySubmissions />
                </Suspense>
              }
              exact
            />
            <Route
              path="/harbor/submissions/:id"
              element={
                <Suspense fallback={<Loader />}>
                  <HarborSubmissionDetails />
                </Suspense>
              }
              exact
            />
            <Route
              path="/harbor/function-access"
              element={
                <Suspense fallback={<Loader />}>
                  <HarborFunctionAccess />
                </Suspense>
              }
              exact
            />
            <Route
              path="/harbor/knowledge-hub"
              element={
                <Suspense fallback={<Loader />}>
                  <HarborKnowledgeHub />
                </Suspense>
              }
              exact
            />
            <Route
              path="/harbor/approver"
              element={
                <Suspense fallback={<Loader />}>
                  <HarborApproverPage />
                </Suspense>
              }
              exact
            />
            <Route
              path="/harbor/approver/:id"
              element={
                <Suspense fallback={<Loader />}>
                  <HarborApproverDetailPage />
                </Suspense>
              }
              exact
            />
            <Route
              path="/harbor/ssot"
              element={
                <Suspense fallback={<Loader />}>
                  <HarborSsot />
                </Suspense>
              }
              exact
            />
          </Route>
          <Route index element={<Home />} />
          <Route path="home" element={<Home />} />
          <Route path="home-2" element={<Home />} />
          <Route
            path="/:tab"
            element={
              <ProtectedRoute page={APP_CONSTANTS.PERSONA_PAGE.SIDE_NAV}>
                <Suspense fallback={<Loader />}>
                  <AppContainer />
                </Suspense>
              </ProtectedRoute>
            }
          />
          <Route
            path="/help/newticket"
            element={
              <Suspense fallback={<Loader />}>
                <NewTicket />
              </Suspense>
            }
            exact
          />
          <Route
            path="/rapid/devops"
            element={
              <ProtectedRoute
                page={APP_CONSTANTS.PERSONA_PAGE.SIDE_NAV}
                optional_endpoint={"/rapid"}
              >
                <Suspense fallback={<Loader />}>
                  <DevOpsLandingPageContainer />
                </Suspense>
              </ProtectedRoute>
            }
            exact
          />
          <Route
            path="/rapid/devops/details/:id"
            element={
              <ProtectedRoute
                page={APP_CONSTANTS.PERSONA_PAGE.SIDE_NAV}
                optional_endpoint={"/rapid"}
              >
                <Suspense fallback={<Loader />}>
                  <DevOpsDetailsPage />
                </Suspense>
              </ProtectedRoute>
            }
            exact
          />
          <Route
            path="/rapid/admin"
            element={
              <ProtectedRoute
                page={APP_CONSTANTS.PERSONA_PAGE.SIDE_NAV}
                optional_endpoint={"/rapid"}
              >
                <Suspense fallback={<Loader />}>
                  <AdminLandingPageContainer />
                </Suspense>
              </ProtectedRoute>
            }
            exact
          />
          <Route
            path="/rapid/register-rapidApplication"
            element={
              <ProtectedRoute
                page={APP_CONSTANTS.PERSONA_PAGE.SIDE_NAV}
                optional_endpoint={"/rapid"}
              >
                <Suspense fallback={<Loader />}>
                  <RapidRegisterContainer />
                </Suspense>
              </ProtectedRoute>
            }
            exact
          />
          <Route
            path="/rapid/edit-rapidApplication/:id"
            element={
              <ProtectedRoute
                page={APP_CONSTANTS.PERSONA_PAGE.SIDE_NAV}
                optional_endpoint={"/rapid"}
              >
                <Suspense fallback={<Loader />}>
                  <RapidRegisterContainer />
                </Suspense>
              </ProtectedRoute>
            }
            exact
          />
          <Route
            path="/rapid/register-rapidApplication/:id"
            element={
              <ProtectedRoute
                page={APP_CONSTANTS.PERSONA_PAGE.SIDE_NAV}
                optional_endpoint={"/rapid"}
              >
                <Suspense fallback={<Loader />}>
                  <RapidEditContainer />
                </Suspense>
              </ProtectedRoute>
            }
            exact
          />
          <Route
            path="/rapid/register-rapidApplication/admin/:id"
            element={
              <ProtectedRoute
                page={APP_CONSTANTS.PERSONA_PAGE.SIDE_NAV}
                optional_endpoint={"/rapid"}
              >
                <Suspense fallback={<Loader />}>
                  <AdminRapidEditContainer />
                </Suspense>
              </ProtectedRoute>
            }
            exact
          />
          <Route
            path="/rapid/edit-finished-rapidApplication/:id"
            element={
              <ProtectedRoute
                page={APP_CONSTANTS.PERSONA_PAGE.SIDE_NAV}
                optional_endpoint={"/rapid"}
              >
                <Suspense fallback={<Loader />}>
                  <EditRapidFinishedContainer />
                </Suspense>
              </ProtectedRoute>
            }
            exact
          />
          <Route
            path="/help/:ticketNumber"
            element={
              <Suspense fallback={<Loader />}>
                <TicketDetails />
              </Suspense>
            }
            exact
          />

          <Route
            path="/:tab/:registerTab"
            element={
              <ProtectedRoute page={APP_CONSTANTS.PERSONA_PAGE.MY_APPS}>
                <Suspense fallback={<Loader />}>
                  <RegisterContainer />
                </Suspense>
              </ProtectedRoute>
            }
          />

          <Route
            path="/my-apps/group-report/:id"
            element={
              <Suspense fallback={<Loader />}>
                <EditGroupReport />
              </Suspense>
            }
          />
          <Route
            path="/:tab/:editApplication/:id"
            element={
              <Suspense fallback={<Loader />}>
                <EditApplication />
              </Suspense>
            }
          />
          {/* TODO: how to know which is opened from report, data asset etc. */}
          <Route
            path="/discover/:id"
            element={
              <Suspense fallback={<Loader />}>
                <ApplicationDetails />
              </Suspense>
            }
            exact
          />
          <Route
            path="/discover/dataproduct/:name"
            element={
              <Suspense fallback={<Loader />}>
                <DataProductDetails />
              </Suspense>
            }
            exact
          />

          <Route
            path="/admin/approvals"
            element={
              <ProtectedRoute page={APP_CONSTANTS.PERSONA_PAGE.ADMIN_DASHBOARD}>
                <Suspense fallback={<Loader />}>
                  <Approvals />
                </Suspense>
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/approvals/details/:id"
            element={
              <ProtectedRoute
                page={APP_CONSTANTS.PERSONA_PAGE.ADMIN_DASHBOARD}
                optional_endpoint={"/admin/approvals/details"}
              >
                <Suspense fallback={<Loader />}>
                  <ApprovalsDetails />
                </Suspense>
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/userinfo"
            element={
              <Suspense fallback={<Loader />}>
                <WorkInProgress />
              </Suspense>
            }
          />
          <Route
            path="/admin/brandmaintenance"
            element={
              <ProtectedRoute page={APP_CONSTANTS.PERSONA_PAGE.ADMIN_DASHBOARD}>
                <Suspense fallback={<Loader />}>
                  <BrandMaintenanceHomePage />
                </Suspense>
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/kpidashboard"
            element={
              <ProtectedRoute page={APP_CONSTANTS.PERSONA_PAGE.ADMIN_DASHBOARD}>
                <Suspense fallback={<Loader />}>
                  <KPIsDashboard />
                </Suspense>
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/dataGovernance"
            element={
              <ProtectedRoute page={APP_CONSTANTS.PERSONA_PAGE.ADMIN_DASHBOARD}>
                <Suspense fallback={<Loader />}>
                  <DataGovernanceHomepage />
                </Suspense>
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/sigviewautomation"
            element={
              <ProtectedRoute page={APP_CONSTANTS.PERSONA_PAGE.ADMIN_DASHBOARD}>
                <Suspense fallback={<Loader />}>
                  <SigviewAutomationLandingPage />
                </Suspense>
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/sigviewautomation/:id"
            element={
              <ProtectedRoute page={APP_CONSTANTS.PERSONA_PAGE.ADMIN_DASHBOARD}>
                <Suspense fallback={<Loader />}>
                  <SigviewAutomationForm />
                </Suspense>
              </ProtectedRoute>
            }
          />
          {/* <Route
            path="/rapid/billing-dashboard"
            element={
              <Suspense fallback={<Loader />}>
                <BillingDashboard />
              </Suspense>
            }
          /> */}
        </Route>
        <Route path="*" element={<PageNotFound />}></Route>
      </Routes>
    </Suspense>
  );
};

export default AppRoutes;
